import React, { useState } from 'react';
import { ReceiptPrinterService } from '../../services/printing/ReceiptPrinterService';

interface USBDeviceInfo {
  vendorId: number;
  productId: number;
  manufacturerName?: string;
  productName?: string;
  serialNumber?: string;
}

export const USBPrinterSetup: React.FC = () => {
  const [availableDevices, setAvailableDevices] = useState<USBDeviceInfo[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<USBDeviceInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [printerService] = useState(() => new ReceiptPrinterService());

  const handleGetDevices = async () => {
    setIsLoading(true);
    setMessage('');
    
    try {
      const devices = await ReceiptPrinterService.getAvailableUSBDevices();
      setAvailableDevices(devices);
      setMessage(`Found ${devices.length} USB device(s)`);
    } catch (error) {
      setMessage(`Error getting devices: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestDevice = async () => {
    setIsLoading(true);
    setMessage('');
    
    try {
      const success = await printerService.connectUSBDevice();
      if (success) {
        // Get device info for display
        const devices = await ReceiptPrinterService.getAvailableUSBDevices();
        const connectedDevice = devices.find(device => 
          printerService.isUSBDeviceConnected()
        );
        
        if (connectedDevice) {
          setSelectedDevice(connectedDevice);
          setMessage(`Connected to: ${connectedDevice.productName || 'Unknown'} (Vendor: 0x${connectedDevice.vendorId.toString(16)}, Product: 0x${connectedDevice.productId.toString(16)})`);
        } else {
          setMessage('Device connected successfully');
        }
      } else {
        setMessage('Failed to connect to USB device');
      }
    } catch (error) {
      setMessage(`Error requesting device: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestPrint = async () => {
    if (!printerService.isUSBDeviceConnected()) {
      setMessage('Please connect to a USB device first');
      return;
    }

    setIsLoading(true);
    setMessage('');
    
    try {
      const testReceiptData = {
        transactionId: 'TEST-001',
        memberName: 'Test Member',
        membershipId: 'TEST123',
        timestamp: new Date(),
        items: [
          {
            name: 'Test Item',
            quantity: 1,
            unitPrice: 10.00,
            total: 10.00
          }
        ],
        subtotal: 10.00,
        total: 10.00,
        status: 'completed',
        clubName: 'Test Club'
      };

      const success = await printerService.printReceipt(testReceiptData);
      
      if (success) {
        setMessage('Test print successful!');
      } else {
        setMessage('Test print failed. Check console for details.');
      }
    } catch (error) {
      setMessage(`Error during test print: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '600px' }}>
      <h2>USB Printer Setup</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={handleGetDevices}
          disabled={isLoading}
          style={{ marginRight: '10px' }}
        >
          {isLoading ? 'Loading...' : 'Get Available Devices'}
        </button>
        
        <button 
          onClick={handleRequestDevice}
          disabled={isLoading}
          style={{ marginRight: '10px' }}
        >
          {isLoading ? 'Loading...' : 'Connect USB Device'}
        </button>
        
        {printerService.isUSBDeviceConnected() && (
          <button 
            onClick={async () => {
              await printerService.disconnectUSBDevice();
              setSelectedDevice(null);
              setMessage('USB device disconnected');
            }}
            style={{ backgroundColor: '#f44336', color: 'white' }}
          >
            Disconnect
          </button>
        )}
      </div>

      {message && (
        <div style={{ 
          padding: '10px', 
          marginBottom: '20px',
          backgroundColor: message.includes('Error') ? '#ffebee' : '#e8f5e8',
          border: `1px solid ${message.includes('Error') ? '#f44336' : '#4caf50'}`,
          borderRadius: '4px'
        }}>
          {message}
        </div>
      )}

      {availableDevices.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h3>Available USB Devices:</h3>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {availableDevices.map((device, index) => (
              <li key={index} style={{ 
                padding: '10px', 
                margin: '5px 0', 
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: selectedDevice?.vendorId === device.vendorId && 
                               selectedDevice?.productId === device.productId ? '#e3f2fd' : 'white'
              }}>
                <strong>{device.productName || 'Unknown Device'}</strong><br />
                Manufacturer: {device.manufacturerName || 'Unknown'}<br />
                Vendor ID: 0x{device.vendorId.toString(16)}<br />
                Product ID: 0x{device.productId.toString(16)}<br />
                {device.serialNumber && `Serial: ${device.serialNumber}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {printerService.isUSBDeviceConnected() && (
        <div style={{ marginBottom: '20px' }}>
          <h3>Connected USB Device:</h3>
          <div style={{ 
            padding: '10px', 
            border: '1px solid #4caf50',
            borderRadius: '4px',
            backgroundColor: '#e8f5e8'
          }}>
            <strong>{selectedDevice?.productName || 'Unknown Device'}</strong><br />
            {selectedDevice && (
              <>
                Vendor ID: 0x{selectedDevice.vendorId.toString(16)}<br />
                Product ID: 0x{selectedDevice.productId.toString(16)}
              </>
            )}
            <div style={{ marginTop: '10px', fontWeight: 'bold', color: '#4caf50' }}>
              ✓ Connected and Ready
            </div>
          </div>
          
          <div style={{ marginTop: '10px' }}>
            <button 
              onClick={async () => {
                setIsLoading(true);
                setMessage('');
                try {
                  const isConnected = await printerService.testUSBConnection();
                  if (isConnected) {
                    setMessage('USB connection test successful! Printer is ready.');
                  } else {
                    setMessage('USB connection test failed. Try reconnecting.');
                  }
                } catch (error) {
                  setMessage(`Connection test error: ${error instanceof Error ? error.message : 'Unknown error'}`);
                } finally {
                  setIsLoading(false);
                }
              }}
              disabled={isLoading}
              style={{ 
                marginRight: '10px',
                padding: '8px 16px',
                backgroundColor: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {isLoading ? 'Testing...' : 'Test Connection'}
            </button>
            
            <button 
              onClick={handleTestPrint}
              disabled={isLoading}
              style={{ 
                padding: '8px 16px',
                backgroundColor: '#2196f3',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {isLoading ? 'Printing...' : 'Test Print'}
            </button>
          </div>
        </div>
      )}

      {!printerService.isUSBDeviceConnected() && (
        <div style={{ 
          marginBottom: '20px', 
          padding: '15px', 
          backgroundColor: '#fff3cd', 
          border: '1px solid #ffeaa7',
          borderRadius: '4px'
        }}>
          <strong>No USB device connected.</strong> Click "Connect USB Device" to select your thermal printer.
        </div>
      )}

      <div style={{ marginTop: '30px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
        <h4>Instructions:</h4>
        <ol>
          <li>Click "Get Available Devices" to see currently connected USB devices</li>
          <li>Click "Request Device Access" to select your thermal printer</li>
          <li>Once a device is selected, click "Test Print" to verify the connection</li>
          <li>If your printer isn't showing up, make sure it's connected and the Web USB API is supported in your browser</li>
        </ol>
        
        <p><strong>Note:</strong> This feature requires HTTPS and a browser that supports the Web USB API (Chrome, Edge, Opera).</p>
      </div>
    </div>
  );
}; 